import React, { useState, useEffect } from 'react';
import apiClient from '../services/api';
import PasswordStrength from './PasswordStrength';
import { confirmAsync } from '../utils/notify';
import './DeviceManagement.css';

function DeviceManagement() {
  const [activeTab, setActiveTab] = useState('sessions'); // 'sessions' or 'password'
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  
  // Password change form state
  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [passwordLoading, setPasswordLoading] = useState(false);

  useEffect(() => {
    fetchSessions();
  }, []);

  // Auto-dismiss success messages after 5 seconds
  useEffect(() => {
    if (message && message.type === 'success') {
      const timer = setTimeout(() => {
        setMessage(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  const fetchSessions = async () => {
    setLoading(true);
    try {
      // Fetch enough sessions to get all active + 10 recent inactive
      const response = await apiClient.get('/api/v1/auth/login-history?limit=100&offset=0');
      setSessions(response.data.items || []);
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Failed to fetch sessions' });
    } finally {
      setLoading(false);
    }
  };


  const handleLogoutSession = async (sessionId) => {
    if (!(await confirmAsync({ message: 'Are you sure you want to logout from this device?', danger: true }))) {
      return;
    }

    try {
      await apiClient.delete(`/api/v1/auth/sessions/${sessionId}`);
      setMessage({ type: 'success', text: 'Session terminated successfully' });
      // Refresh sessions list
      fetchSessions();
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Failed to terminate session' });
    }
  };

  const handleLogoutAllOthers = async () => {
    if (!(await confirmAsync({ message: 'Are you sure you want to logout from all other devices? This will keep only your current session active.', danger: true }))) {
      return;
    }

    try {
      await apiClient.post('/api/v1/auth/sessions/logout-all-others');
      setMessage({ type: 'success', text: 'All other devices logged out successfully' });
      // Refresh sessions list
      fetchSessions();
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Failed to logout other devices' });
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setPasswordLoading(true);
    setMessage(null);

    if (passwordForm.new_password !== passwordForm.confirm_password) {
      setMessage({ type: 'error', text: 'New passwords do not match' });
      setPasswordLoading(false);
      return;
    }

    if (passwordForm.new_password.length < 8) {
      setMessage({ type: 'error', text: 'Password must be at least 8 characters long' });
      setPasswordLoading(false);
      return;
    }

    try {
      await apiClient.post('/api/v1/auth/change-password', passwordForm);
      setMessage({ type: 'success', text: 'Password changed successfully' });
      setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Failed to change password' });
    } finally {
      setPasswordLoading(false);
    }
  };

  const isCurrentSession = (session) => {
    return session.is_current === true;
  };

  const activeSessions = sessions.filter(s => s.is_active);
  const inactiveSessions = sessions.filter(s => !s.is_active);

  return (
    <div className="device-management-container">
      <div className="device-management-card">
        <h2>Account Security</h2>
        <p className="subtitle">Manage your password and active sessions</p>

        {message && (
          <div className={`alert alert-${message.type}`}>
            <span>{message.text}</span>
            <button
              onClick={() => setMessage(null)}
              className="alert-close"
              aria-label="Close"
            >
              ×
            </button>
          </div>
        )}

        <div className="tabs" style={{ display: 'flex', gap: '10px', marginBottom: '20px', borderBottom: '2px solid #eee' }}>
          <button
            className={`tab ${activeTab === 'password' ? 'active' : ''}`}
            onClick={() => setActiveTab('password')}
            style={{
              padding: '10px 20px',
              border: 'none',
              background: 'none',
              cursor: 'pointer',
              borderBottom: activeTab === 'password' ? '2px solid #667eea' : '2px solid transparent',
              color: activeTab === 'password' ? '#667eea' : '#666',
              fontWeight: activeTab === 'password' ? 'bold' : 'normal'
            }}
          >
            Change Password
          </button>
          <button
            className={`tab ${activeTab === 'sessions' ? 'active' : ''}`}
            onClick={() => { setActiveTab('sessions'); fetchSessions(); }}
            style={{
              padding: '10px 20px',
              border: 'none',
              background: 'none',
              cursor: 'pointer',
              borderBottom: activeTab === 'sessions' ? '2px solid #667eea' : '2px solid transparent',
              color: activeTab === 'sessions' ? '#667eea' : '#666',
              fontWeight: activeTab === 'sessions' ? 'bold' : 'normal'
            }}
          >
            Active Sessions
          </button>
        </div>

        {activeTab === 'password' && (
          <div className="password-change-section">
            <form onSubmit={handlePasswordChange} style={{ maxWidth: '500px' }}>
              <div className="form-group" style={{ marginBottom: '20px' }}>
                <label htmlFor="current_password" style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                  Current Password
                </label>
                <input
                  id="current_password"
                  type="password"
                  value={passwordForm.current_password}
                  onChange={(e) => setPasswordForm({ ...passwordForm, current_password: e.target.value })}
                  required
                  disabled={passwordLoading}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                />
              </div>
              
              <div className="form-group" style={{ marginBottom: '20px' }}>
                <label htmlFor="new_password" style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                  New Password
                </label>
                <input
                  id="new_password"
                  type="password"
                  value={passwordForm.new_password}
                  onChange={(e) => setPasswordForm({ ...passwordForm, new_password: e.target.value })}
                  required
                  disabled={passwordLoading}
                  minLength={8}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                />
                {passwordForm.new_password && <PasswordStrength password={passwordForm.new_password} />}
              </div>
              
              <div className="form-group" style={{ marginBottom: '20px' }}>
                <label htmlFor="confirm_password" style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                  Confirm New Password
                </label>
                <input
                  id="confirm_password"
                  type="password"
                  value={passwordForm.confirm_password}
                  onChange={(e) => setPasswordForm({ ...passwordForm, confirm_password: e.target.value })}
                  required
                  disabled={passwordLoading}
                  minLength={8}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                />
                {passwordForm.confirm_password && passwordForm.new_password !== passwordForm.confirm_password && (
                  <div style={{ color: '#e74c3c', fontSize: '12px', marginTop: '5px' }}>
                    Passwords do not match
                  </div>
                )}
              </div>
              
              <button
                type="submit"
                className="btn btn-primary"
                disabled={passwordLoading}
                style={{
                  padding: '10px 20px',
                  background: '#667eea',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: passwordLoading ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: 'bold'
                }}
              >
                {passwordLoading ? 'Changing...' : 'Change Password'}
              </button>
            </form>
          </div>
        )}

        {activeTab === 'sessions' && (
          <>
        <div className="device-management-actions">
          <button
            onClick={handleLogoutAllOthers}
            className="btn btn-danger"
            disabled={activeSessions.length <= 1}
          >
            Logout All Other Devices
          </button>
          <button
            onClick={fetchSessions}
            className="btn btn-secondary"
            disabled={loading}
          >
            {loading ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>

        <div className="sessions-section">
          <h3>Active Sessions ({activeSessions.length})</h3>
          {activeSessions.length === 0 ? (
            <p className="no-sessions">No active sessions</p>
          ) : (
            <div className="sessions-list">
              {activeSessions.map(session => (
                <div
                  key={session.id}
                  className={`session-item ${isCurrentSession(session) ? 'current-session' : ''}`}
                >
                  <div className="session-info">
                    <div className="session-header">
                      <strong>{session.device_name || 'Unknown Device'}</strong>
                      {isCurrentSession(session) && (
                        <span className="current-badge">Current Session</span>
                      )}
                    </div>
                    <div className="session-details">
                      <p><strong>IP Address:</strong> {session.ip_address || 'N/A'}</p>
                      <p><strong>Login Time:</strong> {formatDate(session.login_at)}</p>
                      <p><strong>Last Activity:</strong> {formatDate(session.last_activity_at)}</p>
                      <p><strong>Expires:</strong> {formatDate(session.expires_at)}</p>
                    </div>
                  </div>
                  {!isCurrentSession(session) && (
                    <button
                      onClick={() => handleLogoutSession(session.id)}
                      className="btn btn-small btn-danger"
                    >
                      Logout
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {inactiveSessions.length > 0 && (
          <div className="sessions-section">
            <h3>Recent Sessions ({Math.min(inactiveSessions.length, 10)})</h3>
            <div className="sessions-list">
              {inactiveSessions.slice(0, 10).map(session => (
                <div key={session.id} className="session-item inactive">
                  <div className="session-info">
                    <div className="session-header">
                      <strong>{session.device_name || 'Unknown Device'}</strong>
                      <span className="inactive-badge">Inactive</span>
                    </div>
                    <div className="session-details">
                      <p><strong>IP Address:</strong> {session.ip_address || 'N/A'}</p>
                      <p><strong>Login Time:</strong> {formatDate(session.login_at)}</p>
                      <p><strong>Last Activity:</strong> {formatDate(session.last_activity_at)}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
          </>
        )}
      </div>
    </div>
  );
}

export default DeviceManagement;

